import "./dp-sizing.js";
import { z } from "zod";
//#region src/dp-admin-limits.ts
/** Spend-ceiling upper guard (USD PER HOUR — DEC-COMPUTE-CONTROL-LAYERS Layer B). A fat-finger guard, not a
*  billing rule. Per-HOUR (not per-month) matches `pricePerHourUsd` on `machineTypeCatalogEntrySchema`. */
const SPEND_CEILING_MAX_USD_PER_HOUR = 1e5;
/**
* Spot/preemptible policy the WS-3 compiler applies to a compiled APP pool (clamp on an app node-shape spot
* intent — DEC-COMPUTE-CONTROL-LAYERS Layer B):
*  - 'forbid' — never spot; an app spot-intent is clamped to on-demand.
*  - 'allow'  — honor the app's node-shape spot intent as-is.
* Two values only (lead ratified — no 'require'; the platform never FORCES spot on an app). ABSENT (no limits
* row / field unset) = today's behavior = on-demand (spot is not set anywhere today —
* as-built-node-pools.md "Not set anywhere: … spot/preemptible").
*/
const spotPolicySchema = z.enum(["forbid", "allow"]);
/**
* Per-DP admin compute limits (DEC-S5). EVERY field OPTIONAL — an absent field = "no bound on that axis"
* (DEC-D10: an all-empty object ≡ no row ≡ today's behavior). REPLACE-semantics on PATCH: the caller sends
* the COMPLETE desired limits object (mirrors updateMachineTypes replacing the enabled selection); an
* omitted sub-field CLEARS that bound. `.strict()` — unknown keys rejected (mass-assignment defense).
*
* These are LAYER B (DEC-COMPUTE-CONTROL-LAYERS) — per-DP LIMITS on APP pools, NOT the direct sizing of the
* infra/default-app platform pools (that is Layer A — the existing `spec.infra`/`spec.defaultApp` sizing,
* unchanged; see §13.1). Per-field scope is below: `maxNodesPerPool` governs the DEDICATED pools WS-3
* compiles this PR (DEC-MAXNODES-DEMAND-DEFER); shared demand pools are provisioned by the composition and
* are NOT clamped by it.
*
* WS-3 clamp semantics (SM-2 — WS-3 owns the behavior, these are the LOCKED field meanings):
*  - maxNodesPerPool        — ceiling on each compiled DEDICATED pool's autoscale.maxNodes: min(intentMax,
*                             limit). NOT applied to the shared DEMAND pools this PR (DEC-MAXNODES-DEMAND-
*                             DEFER) — those keep the BAKED composition max (10, or 3 for GPU;
*                             `_machine-type-node-pools.gotmpl:7-8,40`). Threading the cap onto the demand
*                             composition/XRD is Out-of-Scope (overview §5).
*  - allowDedicated=false   — an app `dedicated:true` intent is clamped to the shared pool (not honored).
*  - spotPolicy             — see spotPolicySchema ('forbid'|'allow').
*  - spendCeilingUsdPerHour — ADVISORY bound (SM-6 demand-check banner + recorded); NOT a hard node clamp
*                             (no cost model in scope — see plan §2.3). `null` = explicitly no ceiling
*                             (≡ omitted). WS-3 surfaces it, does not silently cap nodes on it.
*/
const dpAdminLimitsSchema = z.object({
	maxNodesPerPool: z.number().int().min(1).max(100).optional(),
	allowDedicated: z.boolean().optional(),
	spotPolicy: spotPolicySchema.optional(),
	spendCeilingUsdPerHour: z.number().positive().max(SPEND_CEILING_MAX_USD_PER_HOUR).nullable().optional()
}).strict();
//#endregion
export { SPEND_CEILING_MAX_USD_PER_HOUR, dpAdminLimitsSchema, spotPolicySchema };

//# sourceMappingURL=dp-admin-limits.js.map